import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { FaFileDownload, FaEye, FaCalendar, FaSpinner } from 'react-icons/fa';
import Header from '../components/Header';
import { format } from 'date-fns';
import { CURRENCY_SYMBOLS_TEXT } from '../utils/currencySymbols';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import StatementPreviewModal from '../components/StatementPreviewModal';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { collection, query, where, getDocs } from 'firebase/firestore';
import DataTableStructure from '../components/DataTableStructure';

const Statement = () => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGenerated, setIsGenerated] = useState(false);
  const [lastGeneratedRange, setLastGeneratedRange] = useState(null);
  const [expenseRecords, setExpenseRecords] = useState([]);
  const [expenses, setExpenses] = useState({});
  const { currentUser } = useAuth();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [generateProgress, setGenerateProgress] = useState(0);
  const [collapsedTables, setCollapsedTables] = useState({});

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 }
  };

  // Function to fetch and prepare expense records
  const prepareExpenseRecords = () => {
    if (!startDate || !endDate) return [];

    // Set time to start and end of day for accurate comparison
    const startDateTime = new Date(startDate.setHours(0, 0, 0, 0));
    const endDateTime = new Date(endDate.setHours(23, 59, 59, 999));

    // Get all expenses within date range from the expenses object
    const records = Object.entries(expenses)
      .filter(([date]) => {
        const expenseDate = new Date(date);
        return expenseDate >= startDateTime && expenseDate <= endDateTime;
      })
      .flatMap(([date, dayExpenses]) => 
        dayExpenses.map(expense => ({
          ...expense,
          date,
          formattedDate: format(new Date(date), 'dd MMM yyyy'),
          formattedAmount: `${CURRENCY_SYMBOLS_TEXT[expense.currency]}${parseFloat(expense.amount).toFixed(2)}`
        }))
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));

    return records;
  };

  // Update useEffect to fetch both expenses and incomes
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser) return;
      
      try {
        // Fetch expenses
        const expensesRef = doc(db, 'expenses', currentUser.uid);
        const expensesDoc = await getDoc(expensesRef);
        
        if (expensesDoc.exists()) {
          setExpenses(expensesDoc.data().items || {});
        } else {
          setExpenses({});
        }

        // Fetch incomes
        const incomesQuery = query(
          collection(db, 'incomes'),
          where('userId', '==', currentUser.uid)
        );
        const incomesSnapshot = await getDocs(incomesQuery);
        const incomesData = incomesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date.toDate()
        }));
        setIncomeRecords(incomesData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      }
    };

    fetchData();
  }, [currentUser]);

  // Update records when date range changes
  useEffect(() => {
    if (startDate && endDate) {
      const records = prepareExpenseRecords();
      setExpenseRecords(records);
    }
  }, [startDate, endDate]);

  const handleGenerate = () => {
    setIsGenerating(true);
    setGenerateProgress(0);
    setLastGeneratedRange([startDate, endDate]);
    
    // Fetch and prepare expense records
    const expenseRecs = prepareExpenseRecords();
    setExpenseRecords(expenseRecs);

    // Filter income records for the selected date range
    const filteredIncomes = incomeRecords.filter(income => {
      const incomeDate = new Date(income.date);
      return incomeDate >= startDate && incomeDate <= endDate;
    });

    // Simulate generation process with progress updates
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += 5;
      setGenerateProgress(progress);
      
      if (progress >= 100) {
        clearInterval(progressInterval);
        setIsGenerating(false);
        setIsGenerated(true);
        toast.success('Statement generated successfully!');
      }
    }, 100);
  };

  // Calculate totals by currency
  const calculateTotals = () => {
    const totals = {};
    expenseRecords.forEach(expense => {
      if (!totals[expense.currency]) {
        totals[expense.currency] = 0;
      }
      totals[expense.currency] += parseFloat(expense.amount);
    });
    return Object.entries(totals).map(([currency, amount]) => ({
      currency,
      amount,
      formatted: `${CURRENCY_SYMBOLS_TEXT[currency]}${amount.toFixed(2)}`
    }));
  };

  // Custom input component for DatePicker
  const CustomInput = React.forwardRef(({ value, onClick, label }, ref) => (
    <div className="flex flex-col space-y-2">
      <label className="text-gray-700 dark:text-gray-300">{label}</label>
      <div className="relative cursor-pointer" onClick={onClick}>
        <input
          ref={ref}
          value={value}
          readOnly
          className="w-full p-3 pl-3 pr-10 border rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
          placeholder="Select date"
        />
        <FaCalendar className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
      </div>
    </div>
  ));

  // Check if current date range is different from last generated range
  const isNewDateRange = () => {
    if (!lastGeneratedRange || !startDate || !endDate) return true;
    return lastGeneratedRange[0]?.getTime() !== startDate?.getTime() ||
           lastGeneratedRange[1]?.getTime() !== endDate?.getTime();
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    
    // Add logo
    const logoUrl = '/cashsule.png';
    doc.addImage(logoUrl, 'PNG', 15, 10, 30, 30);
    
    // Title and header info
    doc.setFontSize(20);
    doc.text('Cashsule Statement', 50, 26);
    
    doc.setFontSize(10);
    doc.text('Statement Period:', 15, 50);
    doc.text(`${format(startDate, 'dd MMM yyyy')} - ${format(endDate, 'dd MMM yyyy')}`, 50, 50);
    
    doc.text('Account Holder:', 15, 60);
    doc.text(currentUser.email, 50, 60);
    
    // Filter incomes within date range
    const filteredIncomes = incomeRecords.filter(income => {
      const incomeDate = new Date(income.date);
      return incomeDate >= startDate && incomeDate <= endDate;
    });

    // Calculate totals for both incomes and expenses
    const incomeTotals = {};
    filteredIncomes.forEach(income => {
      if (!incomeTotals[income.currency]) {
        incomeTotals[income.currency] = 0;
      }
      incomeTotals[income.currency] += parseFloat(income.amount);
    });

    const expenseTotals = calculateTotals();

    // Add summary section
    doc.text('Statement Summary:', 15, 75);
    let currentY = 85;

    // Add income totals in green
    Object.entries(incomeTotals).forEach(([currency, amount]) => {
      doc.text(`Money In (${currency}):`, 15, currentY);
      doc.setTextColor(0, 150, 0); // Set green color for income amounts
      doc.text(`${CURRENCY_SYMBOLS_TEXT[currency]}${amount.toFixed(2)}`, 50, currentY);
      doc.setTextColor(0, 0, 0); // Reset to black
      currentY += 5;
    });

    // Add expense totals
    expenseTotals.forEach(total => {
      doc.text(`Money Out (${total.currency}):`, 15, currentY);
      doc.text(`${total.formatted}`, 50, currentY);
      currentY += 5;
    });

    currentY += 10;

    // Add income transactions table
    if (filteredIncomes.length > 0) {
      doc.text('Income Transactions:', 15, currentY);
      currentY += 10;

      doc.autoTable({
        startY: currentY,
        head: [['Date', 'Title', 'Category', 'Amount']],
        body: filteredIncomes.map(income => [
          format(new Date(income.date), 'dd MMM yyyy'),
          income.title,
          income.category,
          {
            content: `${CURRENCY_SYMBOLS_TEXT[income.currency]}${income.amount.toFixed(2)}`,
            styles: { textColor: [0, 150, 0] }
          }
        ]),
        styles: { fontSize: 9, cellPadding: 2 },
        headStyles: {
          fillColor: [66, 66, 166],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 30 }, // Date column
          1: { cellWidth: 60 }, // Title column
          2: { cellWidth: 40 }, // Category column
          3: { cellWidth: 40 }  // Amount column - wider to prevent wrapping
        }
      });

      currentY = doc.lastAutoTable.finalY + 10;
    }

    // Add expense transactions table
    if (expenseRecords.length > 0) {
      doc.text('Expense Transactions:', 15, currentY);
      currentY += 10;

      doc.autoTable({
        startY: currentY,
        head: [['Date', 'Title', 'Category', 'Amount']],
        body: expenseRecords.map(record => [
          record.formattedDate,
          record.title,
          record.category,
          record.formattedAmount
        ]),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: {
          fillColor: [66, 66, 166],
          textColor: [255, 255, 255],
          fontStyle: 'bold'
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 30 }, // Date column
          1: { cellWidth: 60 }, // Title column
          2: { cellWidth: 40 }, // Category column
          3: { cellWidth: 40 }  // Amount column - wider to prevent wrapping
        }
      });
    }

    // Add footer
    const pageCount = doc.internal.getNumberOfPages();
    for(let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.text(
        'Generated by Cashsule',
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: 'center' }
      );
      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.width - 20,
        doc.internal.pageSize.height - 10
      );
    }

    return doc;
  };

  // Updated preview handler with loading state
  const handlePreview = async () => {
    setIsPreviewLoading(true);
    try {
      const doc = generatePDF();
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfUrl);
      setIsPreviewOpen(true);
    } catch (error) {
      console.error('Error generating preview:', error);
      toast.error('Failed to generate preview');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // Add download handler
  const handleDownload = () => {
    try {
      const doc = generatePDF();
      const fileName = `statement_${format(startDate, 'yyyyMMdd')}_${format(endDate, 'yyyyMMdd')}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error downloading statement:', error);
      toast.error('Failed to download statement');
    }
  };

  // Add this function to calculate totals including incomes
  const calculateTotalsByType = () => {
    const totals = {
      income: {},
      expense: {}
    };

    // Calculate income totals
    incomeRecords
      .filter(income => {
        const incomeDate = new Date(income.date);
        return incomeDate >= startDate && incomeDate <= endDate;
      })
      .forEach(income => {
        if (!totals.income[income.currency]) {
          totals.income[income.currency] = 0;
        }
        totals.income[income.currency] += parseFloat(income.amount);
      });

    // Calculate expense totals
    expenseRecords.forEach(expense => {
      if (!totals.expense[expense.currency]) {
        totals.expense[expense.currency] = 0;
      }
      totals.expense[expense.currency] += parseFloat(expense.amount);
    });

    return totals;
  };

  // Update prepareCategoryData to group by currency
  const prepareCategoryData = () => {
    if (!expenseRecords.length) return {};
    
    // Group expenses by currency first, then by category
    const categoryTotalsByCurrency = expenseRecords.reduce((acc, expense) => {
      if (!acc[expense.currency]) {
        acc[expense.currency] = {};
      }
      
      const key = expense.category;
      if (!acc[expense.currency][key]) {
        acc[expense.currency][key] = {
          category: expense.category,
          total: 0,
          count: 0
        };
      }
      
      acc[expense.currency][key].total += parseFloat(expense.amount);
      acc[expense.currency][key].count += 1;
      return acc;
    }, {});

    // Convert to formatted data for each currency
    const result = {};
    Object.entries(categoryTotalsByCurrency).forEach(([currency, categories]) => {
      const totalForCurrency = Object.values(categories)
        .reduce((sum, cat) => sum + cat.total, 0);
      
      result[currency] = Object.values(categories).map(item => ({
        category: item.category,
        amount: `${CURRENCY_SYMBOLS_TEXT[currency]}${item.total.toFixed(2)}`,
        transactions: item.count,
        percentage: ((item.total / totalForCurrency) * 100).toFixed(1)
      }));
    });

    return result;
  };

  // prepareCategoryData
  const categoryColumns = [
    {
      Header: 'Category',
      accessor: 'category',
    },
    {
      Header: 'Total Amount',
      accessor: 'amount',
    },
    {
      Header: 'Transactions',
      accessor: 'transactions',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <motion.div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Financial Statement
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            View and export your complete financial activity in a comprehensive statement
          </p>
        </motion.div>
        
        <motion.div 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6"
          variants={itemVariants}
        >
          <div className="space-y-6">
            {/* Date Range Selector */}
            <motion.div variants={itemVariants} className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
              <div className="relative">
                <DatePicker
                  selected={startDate}
                  onChange={date => {
                    setStartDate(date);
                    setIsGenerated(false);
                  }}
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                  maxDate={endDate || new Date()}
                  customInput={<CustomInput label="Start Date" />}
                  popperClassName="react-datepicker-popper"
                  popperPlacement="bottom"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
              <div className="relative">
                <DatePicker
                  selected={endDate}
                  onChange={date => {
                    setEndDate(date);
                    setIsGenerated(false);
                  }}
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                  maxDate={new Date()}
                  customInput={<CustomInput label="End Date" />}
                  popperClassName="react-datepicker-popper"
                  popperPlacement="bottom"
                  dateFormat="dd/MM/yyyy"
                />
              </div>
            </motion.div>

            {/* Generate Button */}
            <motion.div variants={itemVariants} className="flex flex-col items-center space-y-4">
              <button
                onClick={handleGenerate}
                disabled={!startDate || !endDate || isGenerating || !isNewDateRange()}
                className={`flex items-center space-x-2 px-6 py-3 text-white rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
                  ${isGenerating ? 'bg-indigo-600' : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 background-animate hover:scale-105'}`}
                style={{
                  backgroundSize: '200% 200%',
                  animation: 'gradient-shift 5s ease infinite'
                }}
              >
                {isGenerating ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    <span>Generating...</span>
                  </>
                ) : (
                  <span>Generate Statement</span>
                )}
              </button>

              {/* Add Progress Bar */}
              {isGenerating && (
                <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-200"
                    style={{ width: `${generateProgress}%` }}
                  />
                </div>
              )}
            </motion.div>

            {/* Preview and Download Section */}
            {isGenerated && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                transition={{ duration: 0.3 }}
                className="space-y-4"
              >
                <div className="border-t dark:border-gray-700 pt-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Statement Summary
                  </h2>
                  
                  {/* Summary Section */}
                  <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow">
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Date Range</h3>
                      <p className="mt-1 text-lg font-semibold text-gray-800 dark:text-white">
                        {format(startDate, 'dd MMM yyyy')} - {format(endDate, 'dd MMM yyyy')}
                      </p>
                    </div>
                    {Object.entries(calculateTotalsByType().income).map(([currency, amount]) => (
                      <div key={`income-${currency}`} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Money In ({currency})</h3>
                        <p className="mt-1 text-lg font-semibold text-emerald-700 dark:text-emerald-400">
                          {CURRENCY_SYMBOLS_TEXT[currency]}{amount.toFixed(2)}
                        </p>
                      </div>
                    ))}
                    {Object.entries(calculateTotalsByType().expense).map(([currency, amount]) => (
                      <div key={`expense-${currency}`} className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow">
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Money Out ({currency})</h3>
                        <p className="mt-1 text-lg font-semibold text-gray-800 dark:text-white">
                          {CURRENCY_SYMBOLS_TEXT[currency]}{amount.toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={handlePreview}
                      disabled={isPreviewLoading}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-200"
                    >
                      {isPreviewLoading ? (
                        <>
                          <FaSpinner className="animate-spin" />
                          <span className="text-gray-700 dark:text-gray-300">Generating Preview...</span>
                        </>
                      ) : (
                        <>
                          <FaEye className="text-gray-600 dark:text-gray-300" />
                          <span className="text-gray-700 dark:text-gray-300">Preview Statement</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleDownload}
                      className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors duration-200"
                    >
                      <FaFileDownload />
                      <span>Download PDF</span>
                    </button>
                  </div>

                  {/* Category Summary Section - separate currency tables */}
                  <div className="border-t dark:border-gray-700 pt-6 mt-6">
                    {Object.entries(prepareCategoryData())
                      .sort(([, categoriesA], [, categoriesB]) => categoriesB.length - categoriesA.length) // Sort by number of categories
                      .map(([currency, categoryData]) => (
                        <div key={currency} className="mt-4 first:mt-0 border rounded-lg bg-gray-50">
                          <button
                            onClick={() => setCollapsedTables(prev => ({
                              ...prev,
                              [currency]: !prev[currency]
                            }))}
                            className="w-full p-4 flex justify-between items-center text-left"
                          >
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Expenses by Category - {CURRENCY_SYMBOLS_TEXT[currency]}
                              </h3>
                              {categoryData.length > 0 && (
                                <p className="text-sm text-gray-600 mt-1">
                                  {categoryData.length} categories
                                </p>
                              )}
                            </div>
                            <svg
                              className={`w-6 h-6 transform transition-transform duration-300 ${
                                collapsedTables[currency] ? '' : 'rotate-180'
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          <div 
                            className={`transition-all duration-300 ease-in-out ${
                              collapsedTables[currency] ? 'overflow-hidden max-h-0 opacity-0' : 'overflow-y-auto max-h-[900px] opacity-100'
                            }`}
                          >
                            <div className="p-4 pt-0">
                              <div className="bg-white dark:bg-gray-700">
                                <DataTableStructure
                                  columns={categoryColumns}
                                  data={categoryData}
                                  initialPageSize={5}
                                  initialSortBy={[{ id: 'amount', desc: true }]}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>

      <StatementPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        pdfUrl={pdfUrl}
        startDate={startDate}
        endDate={endDate}
        generatePDF={generatePDF}
      />
    </div>
  );
};

export default Statement;
